import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  BookOpen,
  ArrowLeft,
  MessageSquare,
  ChevronRight,
  Loader2,
  CheckCircle2,
  Play,
  Brain,
  Lock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { toast } from 'sonner';

const ModulePage = () => {
  const { moduleId } = useParams();
  const navigate = useNavigate();
  const { user, languageVersion } = useAuth();
  const { t } = useTranslation();
  const [module, setModule] = useState(null);
  const [loading, setLoading] = useState(true);

  // Refetch module when language changes or moduleId changes
  useEffect(() => {
    loadModule();
  }, [moduleId, languageVersion]);

  const loadModule = async () => {
    try {
      const response = await api.getModule(moduleId);
      setModule(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error(t('modulePage.upgradeToAccess'));
        navigate('/curriculum');
      } else {
        toast.error(t('modulePage.couldNotLoadModule'));
      }
    } finally {
      setLoading(false);
    }
  };

  const getLessonProgress = (lessonId) => {
    return user?.lessons_completed?.includes(lessonId);
  };

  const getModuleProgress = () => {
    if (!module?.lessons) return 0;
    const completed = module.lessons.filter(l => getLessonProgress(l.id)).length;
    return (completed / module.lessons.length) * 100;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-ocean-blue" />
      </div>
    );
  }

  if (!module) {
    return null;
  }

  const progress = getModuleProgress();

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-ocean-blue text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => navigate('/curriculum')}
                className="text-white hover:bg-white/10"
                data-testid="back-to-curriculum"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="font-bold line-clamp-1">{t(`lessonTitles.${module.id}_title`, { defaultValue: module.title })}</h1>
                <p className="text-xs text-white/70">{t('modulePage.lessonCount', { count: module.lessons?.length || 0 })}</p>
              </div>
            </div>
            <Badge variant="secondary" className="bg-white/20 text-white border-0">
              {Math.round(progress)}% {t('modulePage.complete')}
            </Badge>
          </div>
        </div>
        <div className="wave-divider h-8" />
      </header>

      {/* Progress Bar */}
      <div className="bg-background px-4 py-3 border-b border-border">
        <div className="max-w-4xl mx-auto">
          <Progress value={progress} className="h-2" />
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Module Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <Card className="rounded-2xl border-0 shadow-lg overflow-hidden">
            <div className="h-2 gradient-ocean" />
            <CardHeader>
              <CardTitle className="text-2xl">{t(`lessonTitles.${module.id}_title`, { defaultValue: module.title })}</CardTitle>
              <CardDescription>{t(`lessonTitles.${module.id}_desc`, { defaultValue: module.description })}</CardDescription>
              {module.emotional_tone && (
                <p className="text-sm text-ocean-blue/80 italic mt-2">
                  &ldquo;{module.emotional_tone}&rdquo;
                </p>
              )}
            </CardHeader>
          </Card>
        </motion.div>

        {/* Lessons List */}
        <div className="space-y-4">
          <h2 className="text-lg font-bold text-foreground flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-ocean-blue" />
            {t('modulePage.lessons')}
          </h2>
          
          {module.lessons?.map((lesson, index) => {
            const completed = getLessonProgress(lesson.id);
            
            return (
              <motion.div
                key={lesson.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Link to={`/curriculum/${moduleId}/lesson/${lesson.id}`}>
                  <Card 
                    className={`rounded-xl border-2 transition-all duration-300 cursor-pointer relative overflow-hidden ${
                      completed 
                        ? 'border-mint bg-mint/5 opacity-70 hover:opacity-100' 
                        : 'border-border hover:border-ocean-blue hover:shadow-lg'
                    }`}
                    data-testid={`lesson-card-${lesson.id}`}
                  >
                    {/* Completed overlay */}
                    {completed && (
                      <div className="absolute inset-0 bg-mint/10 flex items-center justify-center z-10 pointer-events-none">
                        <Badge className="bg-mint text-white border-0 shadow-lg">
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                          {t('modulePage.completed')}
                        </Badge>
                      </div>
                    )}
                    <CardContent className="p-4">
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                          completed ? 'bg-mint' : 'gradient-ocean'
                        }`}>
                          {completed ? (
                            <CheckCircle2 className="w-6 h-6 text-white" />
                          ) : (
                            <span className="text-white font-bold">{index + 1}</span>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className={`font-medium ${completed ? 'text-muted-foreground' : 'text-foreground'}`}>
                            {t(`lessonTitles.lesson_${lesson.id}`, { defaultValue: lesson.title })}
                          </h3>
                          <div className="flex items-center gap-2 mt-1">
                            <MessageSquare className="w-4 h-4 text-muted-foreground" />
                            <span className="text-sm text-muted-foreground">
                              {t('modulePage.interactiveLessonChat')}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <ChevronRight className="w-5 h-5 text-muted-foreground" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
            );
          })}
        </div>

        {/* Ask Minnow CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-12"
        >
          <Card className="rounded-2xl border-0 shadow-lg bg-gradient-to-br from-mint via-mint/80 to-mint/60">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-xl gradient-ocean flex items-center justify-center">
                    <Brain className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-ocean-blue">{t('modulePage.needClarification')}</p>
                    <p className="text-sm text-ocean-blue/70">
                      {t('modulePage.askMinnowPrepare')}
                    </p>
                  </div>
                </div>
                <Button 
                  className="rounded-full gradient-ocean text-white"
                  onClick={() => navigate('/minnow')}
                  data-testid="ask-minnow-btn"
                >
                  {t('modulePage.askMinnow')}
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </main>
    </div>
  );
};

export default ModulePage;
